import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

const getLocalDate = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${today.getFullYear()}-${month}-${day}`;
};

const EFTAgreementModal = ({
    isOpen,
    onAgree,
    onAgreementOpen,
    onClose,
    subscriptionAgreement,
    defaultSignerName,
}) => {
    const [agreementOpened, setAgreementOpened] = useState(false);
    const [openingAgreement, setOpeningAgreement] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState("");
    const [awaitingAgreementReturn, setAwaitingAgreementReturn] =
        useState(false);
    const [signatureName, setSignatureName] = useState(defaultSignerName || "");
    const agreementWindowRef = useRef(null);
    const agreementOpenedAtRef = useRef(0);
    const signingDate = getLocalDate();

    useEffect(() => {
        if(isOpen) {
            setAgreementOpened(false);
            setOpeningAgreement(false);
            setAccepting(false);
            setError("");
            setAwaitingAgreementReturn(false);
            setSignatureName(defaultSignerName || "");
            agreementWindowRef.current = null;
        }
    }, [defaultSignerName, isOpen, subscriptionAgreement?.file]);

    useEffect(() => {
        if(!isOpen || !awaitingAgreementReturn) return undefined;

        const markAgreementReviewed = () => {
            if(Date.now() - agreementOpenedAtRef.current < 500) return;
            setAgreementOpened(true);
            setAwaitingAgreementReturn(false);
            setError("");
        };

        window.addEventListener("focus", markAgreementReviewed);
        const closeCheck = window.setInterval(() => {
            if(agreementWindowRef.current?.closed) {
                markAgreementReviewed();
            }
        }, 500);

        return () => {
            window.removeEventListener("focus", markAgreementReviewed);
            window.clearInterval(closeCheck);
        };
    }, [awaitingAgreementReturn, isOpen]);

    if(!isOpen) return null;

    const handleAgreementOpen = async() => {
        if(!subscriptionAgreement?.file || openingAgreement) return;

        const agreementWindow = window.open(
            "about:blank",
            "_blank",
        );
        if(!agreementWindow) {
            setError("Allow pop-ups to open the Subscription Agreement.");
            return;
        }

        agreementWindowRef.current = agreementWindow;
        setOpeningAgreement(true);
        setAgreementOpened(false);
        setError("");

        try {
            await onAgreementOpen();
            agreementWindow.location.href = subscriptionAgreement.file;
            agreementOpenedAtRef.current = Date.now();
            setAwaitingAgreementReturn(true);
        } catch(openError) {
            agreementWindow?.close();
            setError(
                openError.message || "Unable to open the Subscription Agreement.",
            );
        } finally {
            setOpeningAgreement(false);
        }
    };

    const handleAgree = async() => {
        if(!agreementOpened || !signatureName.trim() || accepting) return;
        setAccepting(true);
        setError("");

        try {
            await onAgree({
                signatureName: signatureName.trim(),
                signatureDate: signingDate,
            });
        } catch(acceptError) {
            setError(
                acceptError.message || "Unable to record your agreement.",
            );
            setAccepting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-poppins">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] overflow-y-auto flex flex-col font-poppins">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-navy">
                        Your Investor Commitments
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                        aria-label="Close investor commitments"
                    >
                        &times;
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4 text-gray-800 leading-relaxed">
                    <p>
                        I have read AQi&apos;s{" "}
                        <a
                            href="/faq/public/investors"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-navy underline hover:text-navy-2"
                        >
                            Investor FAQ
                        </a>{" "}
                        and educational materials.
                    </p>
                    <p>
                        I understand that investing in startups is risky. I could
                        lose my entire investment, and I can afford that loss.
                    </p>
                    <p>
                        I understand that I generally can&apos;t resell these
                        securities for one year, and there may never be a market to
                        sell them. I&apos;m prepared to wait years for a return, or
                        to never get one.
                    </p>
                    <p>
                        I understand that AQi does not give investment advice. I am
                        making my own investment decision.
                    </p>
                    <p>
                        I understand the Reg CF investment limits, and this
                        investment keeps me within my limit across all Reg CF
                        offerings in the past 12 months.
                    </p>
                    <p>
                        I understand I can cancel my investment for any reason until
                        48 hours before the offering deadline. After that, I
                        can&apos;t cancel unless the offering has a material change.
                    </p>
                    <p>
                        I am signing the investment agreement(s) electronically. I
                        authorize the transfer of my funds into escrow, where
                        they&apos;ll be held until the offering closes.
                    </p>

                    <div className="rounded-lg border border-navy/20 bg-[#f0f6fb] p-4">
                        {subscriptionAgreement ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleAgreementOpen}
                                    disabled={openingAgreement}
                                    className="font-semibold text-navy underline hover:text-navy-2 disabled:cursor-wait disabled:opacity-60"
                                >
                                    {openingAgreement
                                        ? "Opening Subscription Agreement..."
                                        : "Open Subscription Agreement"}
                                </button>
                                <p className="mt-2 text-sm text-gray-600">
                                    {agreementOpened
                                        ? "Subscription Agreement reviewed. Complete the signature fields below."
                                        : awaitingAgreementReturn
                                          ? "Review the agreement, then return here to sign."
                                          : "You must open and review this campaign's Subscription Agreement before signing."}
                                </p>
                            </>
                        ) : (
                            <p className="font-medium text-red-700">
                                This campaign does not have a Subscription Agreement
                                available. You cannot continue at this time.
                            </p>
                        )}
                    </div>

                    {agreementOpened && (
                        <div className="rounded-lg border border-gray-200 p-4">
                            <h3 className="font-semibold text-navy">
                                Electronic Signature
                            </h3>
                            <p className="mt-1 text-sm text-gray-600">
                                Type your full legal name below. Selecting I Agree
                                applies this electronic signature to the Subscription
                                Agreement.
                            </p>
                            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                <label className="text-sm font-medium text-gray-700">
                                    Full legal name
                                    <input
                                        type="text"
                                        value={signatureName}
                                        onChange={(event) =>
                                            setSignatureName(event.target.value)
                                        }
                                        maxLength={255}
                                        autoComplete="name"
                                        placeholder="Enter your full legal name"
                                        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                                    />
                                </label>
                                <label className="text-sm font-medium text-gray-700">
                                    Date signed
                                    <input
                                        type="date"
                                        value={signingDate}
                                        readOnly
                                        className="mt-1 w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {error && (
                        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                            {error}
                        </p>
                    )}
                </div>

                <div className="flex justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-100 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAgree}
                        disabled={
                            !agreementOpened ||
                            !signatureName.trim() ||
                            accepting
                        }
                        className="px-6 py-3 rounded-lg font-semibold text-white bg-navy hover:bg-navy-2 transition-all disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                        {accepting ? "Recording Agreement..." : "I Agree"}
                    </button>
                </div>
            </div>
        </div>
    );
};

EFTAgreementModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onAgree: PropTypes.func.isRequired,
    onAgreementOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    subscriptionAgreement: PropTypes.shape({
        file: PropTypes.string.isRequired,
        name: PropTypes.string,
    }),
    defaultSignerName: PropTypes.string,
};

export default EFTAgreementModal;
